import type { CalendarOptions, CalendarRef } from '@fullcalendar/react';
import {
  forwardRef,
  lazy,
  Suspense,
  type ForwardRefExoticComponent,
  type RefAttributes,
} from 'react';
import { cn } from '../lib/utils';
import { Spinner } from './ui/spinner';

export type FullCalendarProps = CalendarOptions & {
  className?: string;
  /** Show FullCalendar's default highlight on today's date. @default true */
  highlightToday?: boolean;
};
export type {
  CalendarApi,
  CalendarOptions,
  CalendarRef,
  DateSelectInfo,
  EventApi,
  EventClickInfo,
  EventDisplayInfo,
  EventDropInfo,
  EventInput,
  EventResizeDoneInfo,
} from '@fullcalendar/react';

const LazyFullCalendar = lazy(() => import('./FullCalendarInner'));

/** Classic theme palette mapped to Solink semantic tokens (follows `.dark` with design tokens). */
const FC_TOKEN_VARS =
  '[--fc-classic-background:rgb(var(--background-surface))] ' +
  '[--fc-classic-faint:rgb(var(--foreground-primary)/0.04)] ' +
  '[--fc-classic-muted:rgb(var(--foreground-primary)/0.08)] ' +
  '[--fc-classic-strong:rgb(var(--foreground-primary)/0.14)] ' +
  '[--fc-classic-foreground:rgb(var(--foreground-primary))] ' +
  '[--fc-classic-faint-foreground:rgb(var(--foreground-muted))] ' +
  '[--fc-classic-muted-foreground:rgb(var(--foreground-secondary))] ' +
  '[--fc-classic-border:rgb(var(--border-muted))] ' +
  '[--fc-classic-strong-border:rgb(var(--border-strong))] ' +
  '[--fc-classic-event:var(--primary)] ' +
  '[--fc-classic-event-contrast:var(--primary-foreground)] ' +
  '[--fc-classic-primary:var(--primary)] ' +
  '[--fc-classic-primary-foreground:var(--primary-foreground)] ' +
  '[--fc-classic-highlight:rgb(var(--background-accent)/0.45)] ' +
  '[--fc-classic-now:rgb(var(--status-red))]';

/**
 * FullCalendar wrapped so `@fullcalendar/react` (and its CSS) load only when
 * this component mounts — not when `@solinkcorp/solink-ui` is imported.
 */
export const FullCalendar: ForwardRefExoticComponent<
  FullCalendarProps & RefAttributes<CalendarRef>
> = forwardRef<CalendarRef, FullCalendarProps>(function FullCalendar(
  { className, highlightToday = true, ...props },
  ref,
) {
  return (
    <div
      data-slot="full-calendar"
      className={cn(
        FC_TOKEN_VARS,
        !highlightToday && '[--fc-classic-today:transparent]',
        className,
      )}
    >
      <Suspense
        fallback={
          <div className="flex flex-1 items-center justify-center p-8">
            <Spinner />
          </div>
        }
      >
        <LazyFullCalendar {...props} ref={ref} />
      </Suspense>
    </div>
  );
});
